import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { UseQueryResult, useQuery } from 'react-query';
import { ClassUsageReportItem, EventType } from 'types';
import { client } from 'utils/api-client';
import { WeekdayType } from 'utils/weekdays';

function useClassUsage({
  period,
  startDate,
  endDate,
  classTypes,
  coachIds,
  weekdays,
  classStartTimeFrom,
  classStartTimeTo,
  groupByClass = false,
  groupByDay = false,
  groupByStartTime = false,
  groupByCoach = false,
  enabled = true,
  csvExport,
}: {
  period: string;
  startDate: Date | null;
  endDate: Date | null;
  classTypes: EventType[];
  coachIds: string[];
  weekdays: WeekdayType['day'][];
  classStartTimeFrom: string | null;
  classStartTimeTo: string | null;
  groupByClass: boolean;
  groupByDay: boolean;
  groupByStartTime: boolean;
  groupByCoach: boolean;
  enabled?: boolean;
  csvExport?: string;
}): UseQueryResult<AxiosResponse<ClassUsageReportItem[]>> {
  const fetchClassUsage = useCallback(() => {
    const params = new URLSearchParams();
    params.append('period', period);
    params.append('start_date', startDate ? dayjs(startDate).format('YYYY-MM-DD') : '');
    params.append('end_date', endDate ? dayjs(endDate).format('YYYY-MM-DD') : '');

    if (classTypes.length > 0) {
      classTypes.forEach(classType => {
        params.append('class_type_ids', classType.eventTypeId);
      });
    }

    if (coachIds.length > 0) {
      coachIds.forEach(coachId => {
        params.append('coach_ids', coachId);
      });
    }

    if (weekdays.length > 0) {
      weekdays.forEach(weekday => {
        params.append('weekdays', weekday.toString());
      });
    }

    if (classStartTimeFrom) {
      params.append('class_start_time_from', classStartTimeFrom);
    }

    if (classStartTimeTo) {
      params.append('class_start_time_to', classStartTimeTo);
    }

    params.append('group_by_class', groupByClass.toString());
    params.append('group_by_day', groupByDay.toString());
    params.append('group_by_start_time', groupByStartTime.toString());
    params.append('group_by_coach', groupByCoach.toString());

    return client.get('/reports/utilization/class-usage', {
      params: params,
    });
  }, [
    period,
    startDate,
    endDate,
    classTypes,
    coachIds,
    weekdays,
    classStartTimeFrom,
    classStartTimeTo,
    groupByClass,
    groupByDay,
    groupByStartTime,
    groupByCoach,
  ]);

  return useQuery({
    queryKey: [
      'class-usage',
      period,
      startDate,
      endDate,
      classTypes,
      coachIds,
      weekdays,
      classStartTimeFrom,
      classStartTimeTo,
      groupByClass,
      groupByDay,
      groupByStartTime,
      groupByCoach,
      csvExport,
    ],
    queryFn: fetchClassUsage,
    enabled,
  });
}

export default useClassUsage;
